import PropTypes from "prop-types"
import React, { useEffect, useCallback, useRef } from "react"

import SimpleBar from "simplebar-react"
import MetisMenu from "metismenujs"
import withRouter from "components/Common/withRouter"
import { Link, useLocation } from "react-router-dom"
import { withTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { selectUser } from "store/features/auth/authSlice"

const SidebarContent = props => {
  const user = useSelector(selectUser)

  const location = useLocation()
  const ref = useRef()
  const path = location.pathname

  const activateParentDropdown = useCallback(item => {
    item.classList.add("active")

    const parent = item.parentElement
    const parent2El = parent.childNodes[1]

    if (parent2El && parent2El.id !== "side-menu") {
      parent2El.classList.add("mm-show")
    }

    if (parent) {
      parent.classList.add("mm-active")
      const parent2 = parent.parentElement

      if (parent2) {
        parent2.classList.add("mm-show")

        const parent3 = parent2.parentElement

        if (parent3) {
          parent3.classList.add("mm-active")
          parent3.childNodes[0].classList.add("mm-active")

          const parent4 = parent3.parentElement

          if (parent4) {
            parent4.classList.add("mm-show")

            const parent5 = parent4.parentElement

            if (parent5) {
              parent5.classList.add("mm-show")
              parent5.childNodes[0].classList.add("mm-active")
            }
          }
        }
      }

      scrollElement(item)
      return false
    }

    scrollElement(item)
    return false
  }, [])

  const removeActivation = items => {
    for (let i = 0; i < items.length; ++i) {
      const item = items[i]
      const parent = items[i].parentElement

      if (item && item.classList.contains("active")) {
        item.classList.remove("active")
      }

      if (parent) {
        const parent2El =
          parent.childNodes &&
          parent.childNodes.length &&
          parent.childNodes[1]
            ? parent.childNodes[1]
            : null

        if (parent2El && parent2El.id !== "side-menu") {
          parent2El.classList.remove("mm-show")
        }

        parent.classList.remove("mm-active")

        const parent2 = parent.parentElement

        if (parent2) {
          parent2.classList.remove("mm-show")

          const parent3 = parent2.parentElement

          if (parent3) {
            parent3.classList.remove("mm-active")
            parent3.childNodes[0].classList.remove("mm-active")

            const parent4 = parent3.parentElement

            if (parent4) {
              parent4.classList.remove("mm-show")

              const parent5 = parent4.parentElement

              if (parent5) {
                parent5.classList.remove("mm-show")
                parent5.childNodes[0].classList.remove("mm-active")
              }
            }
          }
        }
      }
    }
  }

  const activeMenu = useCallback(() => {
    const pathName = location.pathname
    const fullPath = pathName

    let matchingMenuItem = null

    const ul = document.getElementById("side-menu")

    if (!ul) return

    const items = ul.getElementsByTagName("a")

    removeActivation(items)

    for (let i = 0; i < items.length; ++i) {
      if (fullPath === items[i].pathname) {
        matchingMenuItem = items[i]
        break
      }
    }

    if (matchingMenuItem) {
      activateParentDropdown(matchingMenuItem)
    }
  }, [path, activateParentDropdown])

  useEffect(() => {
    if (ref.current) {
      ref.current.recalculate()
    }
  }, [])

  useEffect(() => {
    new MetisMenu("#side-menu")
    activeMenu()
  }, [])

  useEffect(() => {
    activeMenu()
  }, [activeMenu])

  function scrollElement(item) {
    if (item) {
      const currentPosition = item.offsetTop

      if (currentPosition > window.innerHeight) {
        ref.current.getScrollElement().scrollTop =
          currentPosition - 300
      }
    }
  }

  const MenuItem = ({ to, icon, label }) => (
    <li>
      <Link to={to} className="waves-effect d-flex align-items-center">
        <i className={`${icon} sidebar-icon`} />
        <span>{props.t(label)}</span>
      </Link>
    </li>
  )

  const SubMenu = ({ icon, title, children }) => (
    <li>
      <Link
        to="/#"
        className="has-arrow waves-effect d-flex align-items-center"
      >
        <i className={`${icon} sidebar-icon`} />
        <span>{props.t(title)}</span>
      </Link>

      <ul className="sub-menu" aria-expanded="false">
        {children}
      </ul>
    </li>
  )

  const cleanRole = String(user?.role || "").toLowerCase().trim().replace(/[-\s]/g, "_")
  const isAdmin = cleanRole === "super_admin" || cleanRole === "admin"
  const isDeveloper = cleanRole === "developer" || cleanRole === "designer"
  const isProjectManager = cleanRole === "project_manager"
  const isMarketing = cleanRole === "marketing"
  const isHR = cleanRole === "hr"
  const isClient = cleanRole === "client"

  // Show flags for each menu item
  const showDashboard = true
  const showUsers = isAdmin || isHR
  const showLeads = isAdmin || isMarketing || isProjectManager
  const showClients = isAdmin || isMarketing || isProjectManager
  const showProjects = isAdmin || isMarketing || isDeveloper || isProjectManager || isClient
  const showWorkLogs = isAdmin || isDeveloper || isProjectManager
  const showTargets = isAdmin || isMarketing || isDeveloper || isProjectManager
  const showMarketingDashboard = isAdmin
  const showJobCirculars = isAdmin || isHR
  const showCreateJob = isAdmin || isHR
  const showJobApplications = isAdmin || isHR
  const showPayroll = isAdmin || isMarketing || isDeveloper || isProjectManager || isHR
  const showHRSetup = isAdmin || isHR
  const showTransactions = isAdmin

  return (
    <React.Fragment>
      <SimpleBar
        style={{
          maxHeight: "100vh",
          height: "100vh",
          position: "sticky",
          top: 100,
          overflowX: "hidden",
        }}
        ref={ref}
      >
        <div id="sidebar-menu">
          <div className="px-3 pt-3 pb-2"></div>

          <ul className="metismenu list-unstyled" id="side-menu">
            {showDashboard && (
              <MenuItem
                to="/dashboard"
                icon="fas fa-home"
                label="Dashboard"
              />
            )}

            {showUsers && (
              <MenuItem
                to="/all-users"
                icon="fas fa-users"
                label="Users"
              />
            )}

            {showLeads && (
              <MenuItem
                to="/all-leads"
                icon="fas fa-user-plus"
                label="Leads"
              />
            )}

            {showClients && (
              <MenuItem
                to="/all-clients"
                icon="fas fa-handshake"
                label="Clients"
              />
            )}

            {showProjects && (
              <MenuItem
                to="/all-projects"
                icon="fas fa-project-diagram"
                label="Projects"
              />
            )}

            {showWorkLogs && (
              <MenuItem
                to="/worklog"
                icon="fas fa-clipboard-list"
                label="Work Logs"
              />
            )}

            {showTargets && (
              <MenuItem
                to="/targets"
                icon="fas fa-bullseye"
                label="Targets & Achievements"
              />
            )}

            {showMarketingDashboard && (
              <MenuItem
                to="/marketing-dashboard"
                icon="fas fa-chart-line"
                label="Marketing Dashboard"
              />
            )}

            {showJobCirculars && (
              <MenuItem
                to="/all-jobs"
                icon="fas fa-bullhorn"
                label="Job Circulars"
              />
            )}

            {showCreateJob && (
              <MenuItem
                to="/create-job"
                icon="fas fa-plus-circle"
                label="Create Job"
              />
            )}

            {showJobApplications && (
              <MenuItem
                to="/all-job-applications"
                icon="fas fa-briefcase"
                label="Job Applications"
              />
            )}

            {showPayroll && (
              <MenuItem
                to="/payroll"
                icon="fas fa-file-invoice-dollar"
                label="Payroll & Salary"
              />
            )}

            {showHRSetup && (
              <MenuItem
                to="/hr-setup"
                icon="fas fa-calendar-alt"
                label="Weekend & Holidays"
              />
            )}

            {showTransactions && (
              <MenuItem
                to="/transactions"
                icon="fas fa-coins"
                label="Transactions"
              />
            )}
          </ul>
        </div>
      </SimpleBar>
    </React.Fragment>
  )
}

SidebarContent.propTypes = {
  location: PropTypes.object,
  t: PropTypes.any,
}

export default withRouter(withTranslation()(SidebarContent))